import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useAuth, apiError } from "../context/AuthContext";
import { http, useApi } from "../lib/api";
import { useSeo } from "../lib/seo";
import { OrderView, StatusBadge } from "../components/shop/OrderView";
import { ProductCard } from "../components/shop/ProductCard";
import { EmptyState } from "../components/shop/Common";

const Orders = () => {
  const { data, loading } = useApi("/account/orders");
  const [open, setOpen] = useState(null);
  if (loading) return <div className="h-40 rounded-3xl bg-sand animate-pulse" />;
  if (!data?.length) return <EmptyState title="No orders yet" text="Your vacation wardrobe starts with a destination." testid="orders-empty"><Link to="/destinations" className="rounded-full bg-sun px-6 py-3 font-bold text-navy">Start shopping</Link></EmptyState>;
  return (
    <div className="space-y-3" data-testid="orders-list">
      {data.map((o) => (
        <div key={o.order_number} className="rounded-3xl border border-border p-5">
          <button className="w-full flex flex-wrap items-center justify-between gap-3 text-left" onClick={() => setOpen(open === o.order_number ? null : o.order_number)} data-testid={`order-row-${o.order_number}`}>
            <span><b className="text-navy">{o.order_number}</b><span className="block text-sm text-navy/60">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s)</span></span>
            <StatusBadge status={o.status} />
          </button>
          {open === o.order_number && <div className="mt-6 pt-6 border-t border-border"><OrderView o={o} /></div>}
        </div>
      ))}
    </div>
  );
};

const Favorites = () => {
  const { user } = useAuth();
  const { data, loading } = useApi("/account/favorites", { n: user?.favorites?.length });
  if (loading) return <div className="h-40 rounded-3xl bg-sand animate-pulse" />;
  if (!data?.length) return <EmptyState title="No favorites yet" text="Tap the heart on any product to save it here." testid="favorites-empty" />;
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10" data-testid="favorites-grid">{data.map((p) => <ProductCard key={p.slug} p={p} />)}</div>;
};

const BLANK = { name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "", is_default: false };
const FIELDS = [["name", "Full name"], ["line1", "Address line 1"], ["line2", "Address line 2 (optional)"], ["city", "City"], ["state", "State"], ["postal_code", "ZIP code"], ["phone", "Phone (optional)"]];

const Addresses = () => {
  const { user, setUser } = useAuth();
  const [edit, setEdit] = useState(null);
  const list = user.addresses || [];
  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, ...body } = edit;
      const r = id ? await http.put(`/account/addresses/${id}`, body) : await http.post("/account/addresses", body);
      setUser({ ...user, addresses: r.data });
      setEdit(null);
      toast.success("Address saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const del = async (id) => {
    const r = await http.delete(`/account/addresses/${id}`);
    setUser({ ...user, addresses: r.data });
  };
  return (
    <div data-testid="addresses-panel">
      <div className="grid sm:grid-cols-2 gap-4">
        {list.map((a) => (
          <div key={a.id} className="rounded-3xl border border-border p-5 text-sm text-navy" data-testid={`address-card-${a.id}`}>
            <p className="font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-tpink" />{a.name} {a.is_default && <span className="text-[10px] bg-turq/15 text-[#0078a0] rounded-full px-2 py-0.5">DEFAULT</span>}</p>
            <p className="mt-2 text-navy/70">{a.line1} {a.line2}<br />{a.city}, {a.state} {a.postal_code}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEdit(a)} className="flex items-center gap-1 text-[#0078a0] font-semibold" data-testid={`edit-address-${a.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => del(a.id)} className="flex items-center gap-1 text-tpink font-semibold ml-3" data-testid={`delete-address-${a.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        ))}
        <button onClick={() => setEdit(BLANK)} className="rounded-3xl border-2 border-dashed border-border p-5 min-h-36 flex items-center justify-center gap-2 font-semibold text-navy hover:border-navy" data-testid="add-address-button"><Plus className="w-4 h-4" /> Add address</button>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="address-dialog">
          <DialogTitle className="text-navy text-xl">{edit?.id ? "Edit address" : "New address"}</DialogTitle>
          {edit && (
            <form onSubmit={save} className="space-y-3">
              {FIELDS.map(([k, label]) => (
                <input key={k} placeholder={label} required={!["line2", "phone"].includes(k)} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="w-full h-11 rounded-full border-2 border-border px-4 outline-none focus:border-turq" data-testid={`address-${k}-input`} />
              ))}
              <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={edit.is_default} onChange={(e) => setEdit({ ...edit, is_default: e.target.checked })} data-testid="address-default-checkbox" /> Set as default</label>
              <button className="w-full h-12 rounded-full bg-sun font-bold text-navy" data-testid="address-save-button">Save address</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  useEffect(() => {
    setName(user.name);
  }, [user.name]);
  const save = async (e) => {
    e.preventDefault();
    try {
      setUser((await http.put("/account/profile", { name })).data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={save} className="max-w-md space-y-4" data-testid="profile-form">
      <label className="block text-sm font-semibold text-navy">Name<input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full h-12 rounded-full border-2 border-border px-5 outline-none focus:border-turq font-normal" data-testid="profile-name-input" /></label>
      <label className="block text-sm font-semibold text-navy">Email<input value={user.email} disabled className="mt-2 w-full h-12 rounded-full border-2 border-border px-5 bg-sand font-normal" /></label>
      <button className="h-12 px-8 rounded-full bg-sun font-bold text-navy" data-testid="profile-save-button">Save changes</button>
    </form>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  useSeo({ title: "My Account", noindex: true });
  if (user === null) return <div className="h-96" />;
  if (!user) return <Navigate to="/login?next=/account" replace />;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-brush text-3xl text-tpink">Aloha,</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-navy" data-testid="account-greeting">{user.name}</h1>
        </div>
        <div className="flex gap-2">
          {user.role === "admin" && <Link to="/admin" className="h-11 px-5 rounded-full bg-navy text-white font-semibold grid place-items-center" data-testid="account-admin-link">Admin dashboard</Link>}
          <button onClick={logout} className="h-11 px-5 rounded-full border-2 border-navy font-semibold text-navy flex items-center gap-2" data-testid="logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>
      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="bg-sand rounded-full p-1 h-auto max-w-full overflow-x-auto hide-scrollbar justify-start">
          {["orders", "favorites", "addresses", "profile"].map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-full px-5 py-2 capitalize data-[state=active]:bg-navy data-[state=active]:text-white" data-testid={`account-tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="orders" className="mt-8"><Orders /></TabsContent>
        <TabsContent value="favorites" className="mt-8"><Favorites /></TabsContent>
        <TabsContent value="addresses" className="mt-8"><Addresses /></TabsContent>
        <TabsContent value="profile" className="mt-8"><Profile /></TabsContent>
      </Tabs>
    </div>
  );
}
